import { useKernelApi } from 'infrastructure/api';
import { ResourceData } from 'framework/crud';
import { mapSortingParameters } from './utils';
import { EMPLOYEE_TYPES_ALIAS } from './constants';
import { useAuthorization } from 'framework/authorization';
import { READ_LEAD_RECRUITER } from './model';

const RESOURCE_URL = '/employees/suggestions';

export type EmployeeType = {
  alias: string;
  workingEmail: string;
  nameEn: {
    firstName: string;
    middleName: null;
    lastName: string;
    maidenName: null;
  };
  office: string;
  timezone: string;
};

/**
 * Employee suggestions service.
 *
 * */
const useEmployeeSuggestionsService = () => {
  const { isGranted } = useAuthorization();
  const { sendGetRequest, sendPostRequest } = useKernelApi();

  const getLeadRecruiters = async () => {
    if (isGranted(READ_LEAD_RECRUITER)) {
      const response = await sendGetRequest('/companies/1/leadrecruiters/employees');
      return response.json();
    }
    return [];
  };

  const baseSearch = async ({ pageNumber = 0, sort = { 'nameEn.lastName': 'ASC' }, ...params }: ResourceData = {}) => {
    const request = {
      ...params,
      sort: mapSortingParameters(sort),
      pageSize: 30,
      pageNumber,
    };
    const response = await sendPostRequest(`${RESOURCE_URL}/search`, request);
    return response.json();
  };
  const search = async ({ filter = {}, skipDefaultEmployeeFilters = false, ...params }: ResourceData = {}) => {
    const request = {
      ...params,
      filter: {
        ...(skipDefaultEmployeeFilters
          ? {}
          : {
              archivedStatus: {
                eq: 'ACTIVE',
              },
              employeeType: {
                ne: EMPLOYEE_TYPES_ALIAS.CONTRACTOR,
              },
            }),
        ...filter,
      },
    };
    return baseSearch(request);
  };
  const advancedSearch = async ({ filter = {}, ...params } = {}) => {
    return baseSearch({
      ...params,
      type: 'advanced',
      filterGroup: filter,
    });
  };
  const searchWithContractors = async ({ filter = {}, ...params }: ResourceData = {}) => {
    const request = {
      ...params,
      filter: {
        archivedStatus: {
          eq: 'ACTIVE',
        },
        ...filter,
      },
    };

    try {
      return await baseSearch(request);
    } catch (error) {
      return {
        result: [],
        error: error,
        totalPages: 0,
      };
    }
  };

  const searchAll = async (params = {}) => {
    let totalPages = 0;
    let currentPage = 0;
    let result: Dictionary<string>[] = [];

    do {
      // eslint-disable-next-line no-await-in-loop
      const data = await search({
        ...params,
        pageNumber: currentPage,
      });
      // eslint-disable-next-line no-await-in-loop

      currentPage += 1;
      totalPages = data.totalPages;
      result = [...result, ...(data.result || [])];
    } while (currentPage < totalPages);

    return result;
  };

  const searchAllWithContractors = async (params = {}) => {
    let totalPages = 0;
    let currentPage = 0;
    let result: Dictionary<string>[] = [];

    do {
      // eslint-disable-next-line no-await-in-loop
      const data = await searchWithContractors({
        ...params,
        pageNumber: currentPage,
      });

      currentPage += 1;
      totalPages = data.totalPages;
      result = [...result, ...(data.result || [])];
    } while (currentPage < totalPages);

    return result;
  };

  const searchAllAdvanced = async (filter = {}) => {
    let totalPages = 0;
    let currentPage = 0;
    let result: Dictionary<string>[] = [];

    do {
      // eslint-disable-next-line no-await-in-loop
      const data = await advancedSearch({
        filter,
        pageNumber: currentPage,
      } as any);
      // eslint-disable-next-line no-await-in-loop

      currentPage += 1;
      totalPages = data.totalPages;
      result = [...result, ...(data.result || [])];
    } while (currentPage < totalPages);

    return result;
  };

  return {
    search,
    getLeadRecruiters,
    advancedSearch,
    searchAll,
    searchAllWithContractors,
    searchWithContractors,
    searchAllAdvanced,
  };
};

export default useEmployeeSuggestionsService;
